import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LayoutDashboard, CalendarDays, BookOpen, Receipt, FileDown, ChevronLeft, ChevronRight, Building2, Settings, LogOut, ChevronDown, CheckCircle2, Globe, Users, DollarSign, ShieldOff, Cog, Sparkles, Handshake, MoonStar, Menu, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/App";
import { Logo } from "@/components/Logo";
import Portal from "@/components/workspace/Portal";
import DashboardTab from "@/components/workspace/DashboardTab";
import CalendarTab from "@/components/workspace/CalendarTab";
import ReservationsTab from "@/components/workspace/ReservationsTab";
import ExpensesTab from "@/components/workspace/ExpensesTab";
import ReportsTab from "@/components/workspace/ReportsTab";
import CRSReservations from "@/components/workspace/CRSReservations";
import GuestDatabase from "@/components/workspace/GuestDatabase";
import RatesTab from "@/components/workspace/RatesTab";
import AdminProfileMenu from "@/components/workspace/AdminProfileMenu";
import PropertyProfile from "@/components/workspace/PropertyProfile";
import TeamTab from "@/components/workspace/TeamTab";
import HousekeepingTab from "@/components/workspace/HousekeepingTab";
import TravelAgentsTab from "@/components/workspace/TravelAgentsTab";
import NightAuditTab from "@/components/workspace/NightAuditTab";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "calendar", label: "Stay View", icon: CalendarDays },
  { id: "rates", label: "Rate Change", icon: DollarSign },
  { id: "reservations", label: "Reservations", icon: BookOpen },
  { id: "housekeeping", label: "Housekeeping", icon: Sparkles },
  { id: "night-audit", label: "Night Audit", icon: MoonStar },
  { id: "guests", label: "Guest Database", icon: Users },
  { id: "travel-agents", label: "Travel Agents", icon: Handshake },
  { id: "expenses", label: "Expenses", icon: Receipt },
  { id: "reports", label: "Reports", icon: FileDown },
  { id: "profile", label: "Property Config", icon: Cog },
];

const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (m, d) => {
  const [y, mo] = m.split("-").map(Number);
  const dt = new Date(y, mo - 1 + d, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleString("en-IN", { month: "long", year: "numeric" });

export default function PmsWorkspace() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [pid, setPid] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [month, setMonth] = useState(thisMonth());
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [crsOpen, setCrsOpen] = useState(false);
  const [consolidatedOpen, setConsolidatedOpen] = useState(false);
  // Item 7 (Batch 6): user-controlled sidebar collapse. Persisted so it
  // survives reloads. Rail width shrinks from 256px → 56px, freeing screen
  // real-estate for wide views like Stay View's calendar.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("pms_sidebar_collapsed") === "1"; }
    catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    const nx = !c;
    try { localStorage.setItem("pms_sidebar_collapsed", nx ? "1" : "0"); } catch {}
    return nx;
  });
  // Tablet/mobile drawer state — sidebar becomes an off-canvas drawer under
  // 1024px so it doesn't eat 15-20% of an iPad portrait viewport. Auto-
  // closes when the user picks a tab so the tap doesn't feel stuck.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pickTab = (id) => { setTab(id); setDrawerOpen(false); };

  useEffect(() => {
    api.get("/pms/overview").then(({ data }) => {
      setOverview(data);
      const active = data.properties.filter((p) => p.status === "active");
      if (active.length) setPid(active[0].id);
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  // Back-button trap — the host explicitly asked that browser Back inside
  // the PMS always returns to the Dashboard tab, NEVER exits the PMS.
  // Only way out is the Sign Out button. Push a sentinel history entry on
  // mount so there's always something to pop; on popstate, close any open
  // overlay first, otherwise bounce to the Dashboard tab and re-push the
  // sentinel to keep the trap active.
  useEffect(() => {
    const SENTINEL = { pmsTrap: true };
    try { window.history.pushState(SENTINEL, "", window.location.href); } catch {}
    const onPop = () => {
      // Close any open floating overlay in reverse priority.
      if (drawerOpen) { setDrawerOpen(false); }
      else if (switcherOpen) { setSwitcherOpen(false); }
      else if (consolidatedOpen) { setConsolidatedOpen(false); }
      else if (crsOpen) { setCrsOpen(false); }
      else if (tab !== "dashboard") { setTab("dashboard"); }
      // Re-push the sentinel so the trap survives every pop. The user
      // sees nothing — the URL stays on the workspace route.
      try { window.history.pushState(SENTINEL, "", window.location.href); } catch {}
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [tab, crsOpen, consolidatedOpen, switcherOpen, drawerOpen]);

  const seedBookings = useCallback(async () => {
    setSeeding(true);
    try {
      const { data: r } = await api.post("/pms/seed-bookings");
      toast.success(`${r.created} demo bookings pulled in from Atiti, Booking.com & Airbnb`);
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(errMsg(e)); }
    setSeeding(false);
  }, []);

  if (!overview) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="animate-spin text-slate-300" size={32} /></div>;

  const active = overview.properties.filter((p) => p.status === "active");
  const others = overview.properties.filter((p) => p.status !== "active");
  const current = overview.properties.find((p) => p.id === pid);

  if (!active.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
        <div className="text-center max-w-sm">
          <Building2 size={40} className="mx-auto text-slate-300" />
          <h2 className="font-display font-bold text-xl mt-4">No active property yet</h2>
          <p className="text-sm text-slate-500 mt-2">Finish a property's setup and payment first — then the PMS opens for it.</p>
          <button data-testid="ws-goto-setup" onClick={() => nav("/pms")} className="btn-brand rounded-full px-6 py-3 text-sm font-semibold mt-6">Go to setup</button>
        </div>
      </div>
    );
  }

  const common = { pid, month, refreshKey, onSeed: seedBookings, seeding };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Backdrop for the tablet/mobile drawer */}
      {drawerOpen && (
        <div data-testid="ws-drawer-backdrop"
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setDrawerOpen(false)} />
      )}
      <aside data-testid="ws-sidebar"
        className={`${collapsed ? "lg:w-14" : "lg:w-64"} bg-slate-900 text-white flex flex-col shrink-0 overflow-hidden
          lg:sticky lg:top-0 lg:h-screen lg:transition-[width] lg:duration-200 lg:translate-x-0
          fixed inset-y-0 left-0 w-72 max-w-[85vw] z-50 h-screen transition-transform duration-200
          ${drawerOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className={`pt-5 pb-3 flex items-center ${collapsed ? "lg:justify-center lg:px-2" : ""} px-5 justify-between`}>
          <Logo h={28} tag="PMS" />
          {/* Close (tablet drawer) */}
          <button data-testid="ws-drawer-close" onClick={() => setDrawerOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white rounded-lg p-1.5 hover:bg-slate-800/60">
            <X size={18} />
          </button>
          {/* Collapse toggle (desktop) */}
          <button data-testid="sidebar-toggle" onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden lg:inline-flex text-slate-400 hover:text-white rounded-lg p-1.5 hover:bg-slate-800/60">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {(!collapsed || drawerOpen) && (
        <div className="px-4 relative lg:block">
          <button data-testid="property-switcher-button" onClick={() => setSwitcherOpen(!switcherOpen)}
            className="w-full bg-[#131B2A] border border-slate-700/60 rounded-2xl px-4 py-3.5 text-left hover:border-slate-500 transition-colors">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Focused property</p>
            <div className="flex items-center justify-between mt-1">
              <div className="min-w-0">
                <p className="font-display font-bold text-sm truncate">{current?.name}</p>
                <p className="text-[11px] text-slate-400">{current?.city} · {current?.unit_count} units</p>
              </div>
              <ChevronDown size={15} className={`text-slate-400 shrink-0 transition-transform ${switcherOpen ? "rotate-180" : ""}`} />
            </div>
          </button>
          {switcherOpen && (
            <div data-testid="property-switcher-list" className="absolute left-4 right-4 mt-2 bg-[#131B2A] border border-slate-700 rounded-2xl overflow-hidden z-30 shadow-2xl">
              {active.map((p) => (
                <button key={p.id} data-testid={`switch-property-${p.id}`} onClick={() => { setPid(p.id); setSwitcherOpen(false); }}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-800/70 transition-colors flex items-center justify-between ${p.id === pid ? "bg-slate-800/50" : ""}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{p.name}</p>
                    <p className="text-[11px] text-slate-400">{p.city} · {p.unit_count} units</p>
                  </div>
                  {p.id === pid && <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />}
                </button>
              ))}
              {others.map((p) => (
                <div key={p.id} className="px-4 py-3 opacity-40 flex items-center justify-between">
                  <div className="min-w-0"><p className="text-sm truncate">{p.name}</p><p className="text-[11px] text-slate-500">setup incomplete</p></div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        <nav className={`${collapsed && !drawerOpen ? "lg:mt-2 lg:px-2" : "mt-6 px-4"} space-y-1 flex-1 overflow-y-auto sidebar-scroll min-h-0`}>
          {TABS.map(({ id, label, icon: Icon }) => {
            const iconOnly = collapsed && !drawerOpen;
            return (
              <button key={id} data-testid={`ws-tab-${id}`} onClick={() => pickTab(id)} title={iconOnly ? label : undefined}
                className={`w-full flex items-center rounded-xl transition-colors
                  ${iconOnly ? "lg:justify-center lg:px-0 lg:py-2.5" : "gap-3 px-3.5 py-3 lg:py-2.5"}
                  text-sm min-h-[44px] lg:min-h-0
                  ${tab === id ? "bg-brand text-white font-semibold" : "text-slate-400 hover:text-white hover:bg-slate-800/60"}`}>
                <Icon size={16} className="shrink-0" /> {(!collapsed || drawerOpen) && <span className="truncate">{label}</span>}
              </button>
            );
          })}
        </nav>

        <div className={`${collapsed && !drawerOpen ? "lg:px-2" : "px-4"} pb-5 space-y-1 border-t border-slate-800 pt-4`}
          style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
          <button data-testid="ws-back-setup" onClick={() => nav("/pms")} title={(collapsed && !drawerOpen) ? "Add properties & integrations" : undefined}
            className={`w-full flex items-center ${collapsed && !drawerOpen ? "lg:justify-center" : "gap-3 px-3.5"} py-2.5 rounded-xl text-xs text-slate-400 hover:text-white transition-colors min-h-[44px] lg:min-h-0`}>
            <Settings size={14} /> {(!collapsed || drawerOpen) && "Add properties & integrations"}
          </button>
          <button data-testid="ws-logout" onClick={() => { logout(); nav("/"); }} title={(collapsed && !drawerOpen) ? "Log out" : undefined}
            className={`w-full flex items-center ${collapsed && !drawerOpen ? "lg:justify-center" : "gap-3 px-3.5"} py-2.5 rounded-xl text-xs text-slate-500 hover:text-white transition-colors min-h-[44px] lg:min-h-0`}>
            <LogOut size={14} /> {(!collapsed || drawerOpen) && `${user?.email?.split("@")[0]} · Log out`}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-20 px-4 md:px-6 lg:px-8 h-16 flex items-center gap-3 pl-safe pr-safe">
          <button data-testid="ws-drawer-open" onClick={() => setDrawerOpen(true)}
            className="lg:hidden -ml-1 w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-700">
            <Menu size={20} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-display font-bold text-slate-900 leading-tight text-sm md:text-base truncate">{current?.name}</h1>
            <p className="text-[11px] text-slate-400 truncate">{TABS.find((t) => t.id === tab)?.label} · property-focused view</p>
          </div>
          {(tab === "dashboard" || tab === "calendar" || tab === "rates" || tab === "restrictions" || tab === "reports") && (
            <div className="hidden sm:flex items-center gap-1 bg-slate-100 rounded-full px-1.5 py-1 shrink-0">
              <button data-testid="month-prev" onClick={() => setMonth(shiftMonth(month, -1))} className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center text-slate-500"><ChevronLeft size={15} /></button>
              <span data-testid="month-label" className="text-xs font-semibold text-slate-700 px-2 w-28 md:w-32 text-center truncate">{monthLabel(month)}</span>
              <button data-testid="month-next" onClick={() => setMonth(shiftMonth(month, 1))} className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center text-slate-500"><ChevronRight size={15} /></button>
            </div>
          )}
          <AdminProfileMenu user={user} onOpenUserManagement={() => setTab("team")} onOpenCRS={() => setCrsOpen(true)} onOpenConsolidated={() => setConsolidatedOpen(true)} />
        </header>
        {/* Compact month pill for phones — sits below header so it doesn't fight the hamburger */}
        {(tab === "dashboard" || tab === "calendar" || tab === "rates" || tab === "restrictions" || tab === "reports") && (
          <div className="sm:hidden sticky top-16 z-10 bg-white border-b border-slate-200 px-4 py-2 flex justify-center">
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-1.5 py-1">
              <button data-testid="month-prev-m" onClick={() => setMonth(shiftMonth(month, -1))} className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center text-slate-500"><ChevronLeft size={15} /></button>
              <span className="text-xs font-semibold text-slate-700 px-2 w-32 text-center">{monthLabel(month)}</span>
              <button data-testid="month-next-m" onClick={() => setMonth(shiftMonth(month, 1))} className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center text-slate-500"><ChevronRight size={15} /></button>
            </div>
          </div>
        )}
        <div className="p-4 md:p-6 lg:p-8">
          {tab === "dashboard" && <DashboardTab {...common} />}
          {tab === "calendar" && <CalendarTab {...common} onMonthChange={setMonth} />}
          {tab === "rates" && <RatesTab {...common} propertyName={current?.name} />}
          {tab === "reservations" && <ReservationsTab {...common} />}
          {tab === "housekeeping" && <HousekeepingTab {...common} />}
          {tab === "night-audit" && <NightAuditTab pid={pid} propertyName={current?.name} />}
          {tab === "guests" && <GuestDatabase />}
          {tab === "travel-agents" && <TravelAgentsTab pid={pid} propertyName={current?.name} />}
          {tab === "team" && <TeamTab pid={pid} propertyName={current?.name} />}
          {tab === "expenses" && <ExpensesTab {...common} onChanged={() => setRefreshKey((k) => k + 1)} />}
          {tab === "reports" && <ReportsTab {...common} propertyName={current?.name} />}
          {tab === "profile" && <PropertyProfile pid={pid} propertyName={current?.name} />}
        </div>
      </main>
      {crsOpen && (
      <Portal>
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={() => setCrsOpen(false)}>
          <div className="bg-white rounded-2xl w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-4">
              <div className="flex-1">
                <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Cross-property · consolidated bookings</p>
                <h2 className="font-display text-xl font-bold text-slate-900">CRS Reservations</h2>
              </div>
              <button data-testid="crs-dialog-close" onClick={() => setCrsOpen(false)} className="text-slate-400 hover:text-slate-700"><ChevronLeft size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6"><CRSReservations /></div>
          </div>
        </div>
      </Portal>
      )}
      {consolidatedOpen && <ConsolidatedViewDialog onClose={() => setConsolidatedOpen(false)} />}
    </div>
  );
}

function ConsolidatedViewDialog({ onClose }) {
  const [d, setD] = useState(null);
  // Batch-3 Item 4: period toggle. "today" preserves the legacy view; "month"
  // aggregates the current calendar month-to-date; "pick" lets the admin
  // choose any past YYYY-MM to compare historical performance.
  const [period, setPeriod] = useState("today");
  const [pickMonth, setPickMonth] = useState(() => new Date().toISOString().slice(0, 7));
  useEffect(() => {
    setD(null);
    const params = {};
    const now = new Date();
    const toIso = (d) => d.toISOString().slice(0, 10);
    if (period === "month") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      params.from_ = toIso(first);
      params.to = toIso(now);
    } else if (period === "pick" && pickMonth) {
      const [y, m] = pickMonth.split("-").map(Number);
      const first = new Date(y, m - 1, 1);
      const last = new Date(y, m, 0);
      params.from_ = toIso(first);
      params.to = toIso(last);
    }
    api.get("/pms/admin/consolidated", { params }).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  }, [period, pickMonth]);
  const isRange = d?.period?.is_range;
  const label = period === "today" ? "today" : period === "month" ? "this month-to-date"
    : (d?.period ? `${d.period.from} → ${d.period.to}` : "selected period");
  const suffix = isRange ? "" : " today";
  return (
    <Portal>
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-4">
          <div className="flex-1">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Cross-property · roll-up across every property under this admin</p>
            <h2 className="font-display text-xl font-bold text-slate-900">Consolidated View <span className="text-slate-400 font-normal text-sm">· {label}</span></h2>
          </div>
          <button data-testid="cv-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><ChevronLeft size={18} /></button>
        </div>
        <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2 flex-wrap">
          <div data-testid="cv-period-toggle" className="inline-flex bg-white rounded-full p-1 text-[11px] font-semibold border border-slate-200">
            {[{ k: "today", label: "Today" }, { k: "month", label: "This Month" }, { k: "pick", label: "Pick Month" }].map((t) => (
              <button key={t.k} data-testid={`cv-period-${t.k}`} onClick={() => setPeriod(t.k)}
                className={`px-3 py-1.5 rounded-full transition-colors ${period === t.k ? "bg-[#4F46E5] text-white shadow" : "text-slate-500 hover:text-slate-800"}`}>{t.label}</button>
            ))}
          </div>
          {period === "pick" && (
            <input data-testid="cv-month-picker" type="month" value={pickMonth}
              max={new Date().toISOString().slice(0, 7)}
              onChange={(e) => setPickMonth(e.target.value)}
              className="text-[11px] border border-slate-200 rounded-full px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
          )}
          <span className="ml-auto text-[10px] text-slate-500">{d?.period ? `${d.period.nights} night${d.period.nights > 1 ? "s" : ""} in window` : ""}</span>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!d ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { k: "arrivals", label: `Arrivals${suffix}` },
                  { k: "departures", label: `Departures${suffix}` },
                  { k: "in_house", label: isRange ? "In-house nights" : "In-house tonight" },
                  { k: "occupancy_pct", label: "Occupancy %", fmt: (v) => `${v}%` },
                  { k: "revenue_today", label: `Revenue${suffix} (₹)`, fmt: (v) => Number(v).toLocaleString("en-IN") },
                  { k: "cost_today", label: `Expenses${suffix} (₹)`, fmt: (v) => Number(v).toLocaleString("en-IN") },
                  { k: "net_today", label: `Net${suffix} (₹)`, fmt: (v) => Number(v).toLocaleString("en-IN") },
                  { k: "properties_live", label: "Properties live" },
                ].map((t) => (
                  <div key={t.k} className="bg-white rounded-2xl border border-slate-200 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t.label}</p>
                    <p className="font-display text-2xl font-bold text-slate-900 mt-1">{t.fmt ? t.fmt(d[t.k] || 0) : (d[t.k] || 0)}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <h3 className="font-display font-bold text-slate-900 text-sm mb-3">Per-property snapshot</h3>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                      <th className="text-left py-2 font-semibold">Property</th>
                      <th className="text-right font-semibold">Arrivals</th>
                      <th className="text-right font-semibold">Departures</th>
                      <th className="text-right font-semibold">{isRange ? "In-house nights" : "In-house"}</th>
                      <th className="text-right font-semibold">Occ %</th>
                      <th className="text-right font-semibold">Revenue ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.per_property || []).map((p) => (
                      <tr key={p.property_id} className="border-b border-slate-50">
                        <td className="py-2.5 font-semibold text-slate-900">{p.name}</td>
                        <td className="py-2.5 text-right font-mono">{p.arrivals}</td>
                        <td className="py-2.5 text-right font-mono">{p.departures}</td>
                        <td className="py-2.5 text-right font-mono">{p.in_house}</td>
                        <td className="py-2.5 text-right font-mono">{p.occupancy_pct}%</td>
                        <td className="py-2.5 text-right font-mono">{Number(p.revenue_today || 0).toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </Portal>
  );
}
